import type { DbExecutor } from "./executor";
import { effectiveFeatureQuantity } from "../domain/feature";
import { generateId } from "./ids";
import {
  deriveBidPackage,
  renderComparableBody,
  renderIssuedScopeBody,
  resolveQuoteAmounts,
  type BidDecision,
  type BidManualItem,
  type BidPackageModel,
  type BidPresentation,
  type BidPresentationMode,
  type BidScope,
  type BidTradeNote,
} from "../engine/bidPackage";
import type { TradeId } from "../domain/trade";

/**
 * THE BID PACKAGE, ASSEMBLED FROM WHAT IS ALREADY TRUE (spec §68A).
 *
 * This reads the renovation and the decisions somebody made about how to
 * contract it, and hands both to the pure engine. It never stores a scope:
 * the current package is always derived, so a design change is reflected
 * the moment it happens and no second copy of the renovation can drift.
 *
 * What it does persist is decisions, Q numbers, manual work, ISSUED
 * packages and the quotes that came back — history that must not move when
 * the design later does.
 *
 * PERFORMANCE (spec §68AD). The whole model is read in a fixed number of
 * queries — eight, whatever the size of the project — because the last
 * phase taught this codebase what per-row reads cost when every statement
 * is a network round trip.
 */

export interface BidCalloutPlacement {
  rootId: string;
  number: number;
  labelX: number | null;
  labelY: number | null;
  /** Set only by manual placement; lets a label stand in for a missing target. */
  floorId: string | null;
}

export interface BidPackageSummary {
  id: string;
  label: string;
  kind: string;
  trades: TradeId[];
  designId: string | null;
  designRevision: number | null;
  createdAt: string;
  scopeCount: number;
}

export interface ContractorQuoteLine {
  id: string;
  groupKey: string;
  label: string;
  materialCents: number | null;
  laborCents: number | null;
  totalCents: number;
  materialResponsibility: "contractor" | "owner" | "mixed" | null;
}

export interface ContractorQuote {
  id: string;
  projectId: string;
  designId: string | null;
  packageId: string | null;
  contractor: string;
  quotedOn: string | null;
  amountCents: number;
  /** Components of amountCents (spec §79G); null = the contractor did not split. */
  materialCents: number | null;
  laborCents: number | null;
  /** The per-work-item breakdown, when one was recorded (§79K). */
  lines: ContractorQuoteLine[];
  notes: string;
  selectedAt: string | null;
  createdAt: string;
  /** Scope keys this one price covers — a trade, an item, or several. */
  coverage: string[];
  attachmentIds: string[];
}

interface DecisionRow {
  root_id: string;
  trade_override: string | null;
  style_override: string | null;
  excluded: number;
  note: string;
  callout_suppressed: number;
}

interface TradeRow {
  trade_id: string;
  note: string;
  style: string | null;
  excluded: number;
  combine_with: string | null;
  performer: string | null;
}

interface ManualRow {
  id: string;
  trade_id: string;
  title: string;
  description: string;
  room_id: string | null;
  itemised: number;
  sort_order: number;
}

interface FeatureRow {
  id: string;
  origin_id: string | null;
  feature_definition_id: string | null;
  name: string;
  action: string;
  room_id: string | null;
  floor_id: string | null;
  quantity_x100: number | null;
  quantity_unit: string | null;
  quantity_geometry_x100: number | null;
  quantity_geometry_binding: string | null;
  quantity_source: string | null;
  notes: string;
  sort_order: number;
}

export class BidService {
  constructor(private readonly db: DbExecutor) {}

  /* ---------------- the current package ---------------- */

  /**
   * The bid package as the project stands right now. Eight queries,
   * regardless of how much renovation there is.
   */
  async model(
    projectId: string,
    designId: string | null,
    options: { onlyTrades?: TradeId[] | null } = {},
  ): Promise<BidPackageModel> {
    const [featureRows, roomRows, decisionRows, tradeRows, manualRows, calloutRows, presentationRows, labelRows, componentRows, selectionRows] = await Promise.all([
      this.db.select<FeatureRow>(
        `SELECT id, origin_id, feature_definition_id, name, action, room_id, floor_id,
                quantity_x100, quantity_unit, quantity_geometry_x100,
                quantity_geometry_binding, quantity_source, notes, sort_order
           FROM project_features
          WHERE project_id = ? AND archived_at IS NULL
            AND (design_id IS NULL OR design_id = ?)
          ORDER BY sort_order, created_at`,
        [projectId, designId],
      ),
      this.db.select<{ id: string; name: string; floor_id: string; floor_name: string }>(
        `SELECT r.id, r.name, r.floor_id, f.name AS floor_name
           FROM rooms r JOIN floors f ON f.id = r.floor_id
          WHERE f.project_id = ? AND r.archived_at IS NULL`,
        [projectId],
      ),
      this.db.select<DecisionRow>(
        `SELECT root_id, trade_override, style_override, excluded, note, callout_suppressed
           FROM bid_decisions WHERE project_id = ? AND (design_id IS ? OR design_id = ?)`,
        [projectId, designId, designId],
      ),
      this.db.select<TradeRow>(
        `SELECT trade_id, note, style, excluded, combine_with, performer
           FROM bid_trade_settings WHERE project_id = ? AND (design_id IS ? OR design_id = ?)`,
        [projectId, designId, designId],
      ),
      this.db.select<ManualRow>(
        `SELECT id, trade_id, title, description, room_id, itemised, sort_order
           FROM bid_manual_items
          WHERE project_id = ? AND archived_at IS NULL AND (design_id IS ? OR design_id = ?)
          ORDER BY sort_order`,
        [projectId, designId, designId],
      ),
      this.db.select<{ root_id: string; number: number }>(
        `SELECT root_id, number FROM bid_callouts WHERE project_id = ? AND (design_id IS ? OR design_id = ?)`,
        [projectId, designId, designId],
      ),
      // Spec §76B: how each scope is shown. One more query, never one per
      // scope — the eight-query rule this service was built on holds.
      this.db.select<{ scope_key: string; mode: string; custom_text: string; custom_digest: string | null }>(
        `SELECT scope_key, mode, custom_text, custom_digest
           FROM bid_presentations WHERE project_id = ? AND (design_id IS ? OR design_id = ?)`,
        [projectId, designId, designId],
      ),
      // Spec §79R: work-item wording — the SAME words the Renovation Budget
      // shows for the same §78 identity. One more fixed query.
      this.db.select<{ group_key: string; label: string }>(
        "SELECT group_key, label FROM budget_line_labels WHERE project_id = ?",
        [projectId],
      ),
      /*
       * Spec §80-9: what the work INCLUDES, from the assemblies it already
       * carries. Names, roles and inclusion only — never a cost. An
       * EXCLUDED material component is a decision somebody made, and it is
       * what lets the sheet say "supplied by owner" without guessing.
       */
      this.db.select<{
        feature_id: string; name: string; role: string; component_type: string; included: number; sort_order: number;
      }>(
        `SELECT i.project_feature_id AS feature_id, c.name, c.role, c.component_type, c.included, c.sort_order
           FROM assembly_instance_components c
           JOIN assembly_instances i ON i.id = c.instance_id
           JOIN project_features f ON f.id = i.project_feature_id
          WHERE f.project_id = ? AND f.archived_at IS NULL
            AND i.archived_at IS NULL AND c.archived_at IS NULL
          ORDER BY i.created_at, c.sort_order`,
        [projectId],
      ),
      // Spec §80-11: the product actually selected is a SPECIFICATION the
      // contractor should match. The snapshot's price is deliberately not read.
      this.db.select<{ feature_id: string; product_name_snapshot: string }>(
        `SELECT i.project_feature_id AS feature_id, s.product_name_snapshot
           FROM project_product_selections s
           JOIN assembly_instance_components c ON c.id = s.component_id
           JOIN assembly_instances i ON i.id = c.instance_id
           JOIN project_features f ON f.id = i.project_feature_id
          WHERE f.project_id = ? AND f.archived_at IS NULL AND s.replaced_at IS NULL
            AND i.archived_at IS NULL AND c.archived_at IS NULL`,
        [projectId],
      ),
    ]);
    const inclusionsByFeature = new Map<string, string[]>();
    const ownerSuppliedByFeature = new Map<string, string[]>();
    const materialFeatures = new Set<string>();
    for (const row of componentRows) {
      if (Number(row.included) === 1 && row.component_type === "material") materialFeatures.add(row.feature_id);
      const target = Number(row.included) === 1 ? inclusionsByFeature : ownerSuppliedByFeature;
      // Only a MATERIAL component that was switched off says anything about
      // who supplies material; an excluded labour line is just work we are
      // not buying, and claiming otherwise would be a guess (§80-12).
      if (Number(row.included) !== 1 && row.component_type !== "material") continue;
      const list = target.get(row.feature_id) ?? [];
      list.push(row.name);
      target.set(row.feature_id, list);
    }
    const specificationsByFeature = new Map<string, string[]>();
    for (const row of selectionRows) {
      const list = specificationsByFeature.get(row.feature_id) ?? [];
      list.push(row.product_name_snapshot);
      specificationsByFeature.set(row.feature_id, list);
    }

    const assignedCallouts: Record<string, number> = {};
    for (const row of calloutRows) assignedCallouts[row.root_id] = Number(row.number);

    return deriveBidPackage({
      features: featureRows.map((row) => ({
        id: row.id,
        // The work's identity across duplicated designs — what a Q number
        // follows, and what a decision is filed against (§68X).
        rootId: row.origin_id ?? row.id,
        featureDefinitionId: row.feature_definition_id,
        name: row.name,
        action: row.action,
        roomId: row.room_id,
        floorId: row.floor_id,
        // The geometry-derived figure when there is one, else the manual
        // one. Never a guess: null stays null and prints as nothing.
        /*
         * ONE RULE FOR "HOW MUCH" (spec §7, reused): a geometry-driven
         * feature carries its unit in the BINDING, not in quantity_unit —
         * a measured room area is square feet whether or not anybody typed
         * the word. Reading the raw column instead left eighteen measured
         * rooms with no unit, so a flooring contractor was handed a sheet
         * with no square footage on it.
         */
        quantityX100: effectiveFeatureQuantity({
          quantityX100: row.quantity_x100 === null ? null : Number(row.quantity_x100),
          quantityUnit: (row.quantity_unit as never) ?? null,
          quantitySource: (row.quantity_source as never) ?? null,
          quantityGeometryBinding: (row.quantity_geometry_binding as never) ?? null,
          quantityGeometryX100:
            row.quantity_geometry_x100 === null || row.quantity_geometry_x100 === undefined
              ? null
              : Number(row.quantity_geometry_x100),
        }).valueX100,
        quantityUnit: effectiveFeatureQuantity({
          quantityX100: row.quantity_x100 === null ? null : Number(row.quantity_x100),
          quantityUnit: (row.quantity_unit as never) ?? null,
          quantitySource: (row.quantity_source as never) ?? null,
          quantityGeometryBinding: (row.quantity_geometry_binding as never) ?? null,
          quantityGeometryX100:
            row.quantity_geometry_x100 === null || row.quantity_geometry_x100 === undefined
              ? null
              : Number(row.quantity_geometry_x100),
        }).unit as never,
        notes: row.notes ?? "",
        sortOrder: Number(row.sort_order),
      })),
      locations: roomRows.map((row) => ({
        roomId: row.id,
        roomName: row.name,
        floorId: row.floor_id,
        floorName: row.floor_name,
      })),
      decisions: decisionRows.map(
        (row): BidDecision => ({
          rootId: row.root_id,
          tradeId: (row.trade_override as TradeId | null) ?? null,
          style: (row.style_override as never) ?? null,
          excluded: Number(row.excluded) === 1,
          note: row.note || null,
          calloutSuppressed: Number(row.callout_suppressed) === 1,
        }),
      ),
      tradeNotes: tradeRows.map(
        (row): BidTradeNote => ({
          tradeId: row.trade_id as TradeId,
          note: row.note ?? "",
          style: (row.style as never) ?? null,
          excluded: Number(row.excluded) === 1,
          combineWith: (row.combine_with as TradeId | null) ?? null,
          performer: row.performer ?? null,
        }),
      ),
      manualItems: manualRows.map(
        (row): BidManualItem => ({
          id: row.id,
          tradeId: row.trade_id as TradeId,
          title: row.title,
          description: row.description,
          roomId: row.room_id,
          itemised: Number(row.itemised) === 1,
          sortOrder: Number(row.sort_order),
        }),
      ),
      assignedCallouts,
      onlyTrades: options.onlyTrades ?? null,
      workItemLabels: new Map(labelRows.map((row) => [row.group_key, row.label])),
      inclusionsByFeature,
      specificationsByFeature,
      ownerSuppliedByFeature,
      materialFeatures,
      presentations: presentationRows.map(
        (row): BidPresentation => ({
          scopeKey: row.scope_key,
          mode: row.mode as BidPresentationMode,
          customText: row.custom_text || null,
          customDigest: row.custom_digest,
        }),
      ),
    });
  }

  /* Spec §74B: the Q-number / plan-callout machinery is gone. Stored
     bid_callouts rows from earlier builds are simply unused. */

  /* ---------------- decisions ---------------- */

  async setDecision(
    projectId: string,
    designId: string | null,
    rootId: string,
    patch: Partial<Omit<BidDecision, "rootId">>,
  ): Promise<void> {
    const now = new Date().toISOString();
    const existing = await this.db.select<{ id: string }>(
      `SELECT id FROM bid_decisions WHERE project_id = ? AND (design_id IS ? OR design_id = ?) AND root_id = ?`,
      [projectId, designId, designId, rootId],
    );
    if (existing.length === 0) {
      await this.db.execute(
        `INSERT INTO bid_decisions
           (id, project_id, design_id, root_id, trade_override, style_override, excluded, note, callout_suppressed, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          generateId(),
          projectId,
          designId,
          rootId,
          patch.tradeId ?? null,
          patch.style ?? null,
          patch.excluded ? 1 : 0,
          patch.note ?? "",
          patch.calloutSuppressed ? 1 : 0,
          now,
          now,
        ],
      );
      return;
    }
    const sets: string[] = [];
    const params: unknown[] = [];
    if ("tradeId" in patch) { sets.push("trade_override = ?"); params.push(patch.tradeId ?? null); }
    if ("style" in patch) { sets.push("style_override = ?"); params.push(patch.style ?? null); }
    if ("excluded" in patch) { sets.push("excluded = ?"); params.push(patch.excluded ? 1 : 0); }
    if ("note" in patch) { sets.push("note = ?"); params.push(patch.note ?? ""); }
    if ("calloutSuppressed" in patch) { sets.push("callout_suppressed = ?"); params.push(patch.calloutSuppressed ? 1 : 0); }
    if (sets.length === 0) return;
    sets.push("updated_at = ?");
    params.push(now, existing[0].id);
    await this.db.execute(`UPDATE bid_decisions SET ${sets.join(", ")} WHERE id = ?`, params);
  }

  async setTradeSetting(
    projectId: string,
    designId: string | null,
    tradeId: TradeId,
    patch: Partial<Omit<BidTradeNote, "tradeId">>,
  ): Promise<void> {
    const now = new Date().toISOString();
    const existing = await this.db.select<{ id: string }>(
      `SELECT id FROM bid_trade_settings WHERE project_id = ? AND (design_id IS ? OR design_id = ?) AND trade_id = ?`,
      [projectId, designId, designId, tradeId],
    );
    if (existing.length === 0) {
      await this.db.execute(
        `INSERT INTO bid_trade_settings
           (id, project_id, design_id, trade_id, note, style, excluded, combine_with, performer, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          generateId(),
          projectId,
          designId,
          tradeId,
          patch.note ?? "",
          patch.style ?? null,
          patch.excluded ? 1 : 0,
          patch.combineWith ?? null,
          patch.performer ?? null,
          now,
          now,
        ],
      );
      return;
    }
    const sets: string[] = [];
    const params: unknown[] = [];
    if ("note" in patch) { sets.push("note = ?"); params.push(patch.note ?? ""); }
    if ("style" in patch) { sets.push("style = ?"); params.push(patch.style ?? null); }
    if ("excluded" in patch) { sets.push("excluded = ?"); params.push(patch.excluded ? 1 : 0); }
    if ("combineWith" in patch) { sets.push("combine_with = ?"); params.push(patch.combineWith ?? null); }
    // §80-5: blank restores the trade's own wording, never an empty line.
    if ("performer" in patch) {
      sets.push("performer = ?");
      const value = (patch.performer ?? "").trim();
      params.push(value === "" ? null : value);
    }
    if (sets.length === 0) return;
    sets.push("updated_at = ?");
    params.push(now, existing[0].id);
    await this.db.execute(`UPDATE bid_trade_settings SET ${sets.join(", ")} WHERE id = ?`, params);
  }

  /* ---------------- presentation (spec §76B/G) ---------------- */

  /**
   * How one scope is shown to a contractor. Presentation only: this
   * changes the words on the sheet and NEVER which features the scope
   * covers, what they cost, or what a quote against them means.
   *
   * A custom wording is stored with the digest of the work it was written
   * about, so §76H can report a later change instead of overwriting what
   * somebody wrote.
   */
  async setPresentation(
    projectId: string,
    designId: string | null,
    scopeKey: string,
    patch: { mode?: BidPresentationMode; customText?: string | null; digest?: string | null },
  ): Promise<void> {
    const now = new Date().toISOString();
    const existing = await this.db.select<{ id: string }>(
      `SELECT id FROM bid_presentations WHERE project_id = ? AND (design_id IS ? OR design_id = ?) AND scope_key = ?`,
      [projectId, designId, designId, scopeKey],
    );
    if (existing.length === 0) {
      await this.db.execute(
        `INSERT INTO bid_presentations
           (id, project_id, design_id, scope_key, mode, custom_text, custom_digest, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          generateId(), projectId, designId, scopeKey,
          patch.mode ?? "generalized",
          patch.customText ?? "",
          patch.digest ?? null,
          now, now,
        ],
      );
      return;
    }
    const sets: string[] = [];
    const params: unknown[] = [];
    if (patch.mode !== undefined) { sets.push("mode = ?"); params.push(patch.mode); }
    if (patch.customText !== undefined) { sets.push("custom_text = ?"); params.push(patch.customText ?? ""); }
    if (patch.digest !== undefined) { sets.push("custom_digest = ?"); params.push(patch.digest); }
    if (sets.length === 0) return;
    sets.push("updated_at = ?");
    params.push(now, existing[0].id);
    await this.db.execute(`UPDATE bid_presentations SET ${sets.join(", ")} WHERE id = ?`, params);
  }

  /* ---------------- manual work ---------------- */

  async addManualItem(
    projectId: string,
    designId: string | null,
    input: Omit<BidManualItem, "id">,
  ): Promise<string> {
    const id = generateId();
    const now = new Date().toISOString();
    await this.db.execute(
      `INSERT INTO bid_manual_items
         (id, project_id, design_id, trade_id, title, description, room_id, itemised, sort_order, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id,
        projectId,
        designId,
        input.tradeId,
        input.title,
        input.description,
        input.roomId,
        input.itemised ? 1 : 0,
        input.sortOrder,
        now,
        now,
      ],
    );
    return id;
  }

  async archiveManualItem(id: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute("UPDATE bid_manual_items SET archived_at = ?, updated_at = ? WHERE id = ?", [now, now, id]);
  }

  /* ---------------- issuing a package ---------------- */

  /**
   * Freeze what was sent (§68Q). The design will change afterwards and this
   * must not: a contractor priced what they were given.
   */
  async issuePackage(options: {
    projectId: string;
    designId: string | null;
    label: string;
    kind: "full" | "trade" | "custom";
    trades: TradeId[];
    designRevision: number | null;
    model: BidPackageModel;
  }): Promise<string> {
    const id = generateId();
    const now = new Date().toISOString();
    await this.db.transaction(async () => {
      await this.db.execute(
        `INSERT INTO bid_packages (id, project_id, design_id, label, kind, trades, design_revision, created_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
        [id, options.projectId, options.designId, options.label, options.kind, options.trades.join(","), options.designRevision, now],
      );
      let sortOrder = 0;
      for (const scope of options.model.scopes) {
        await this.db.execute(
          `INSERT INTO bid_package_scopes
             (id, package_id, scope_key, kind, trade_id, title, callout, body, root_ids, sort_order,
              presentation_mode, summary_text, comparable_body)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            generateId(),
            id,
            scope.key,
            scope.kind,
            scope.tradeId,
            scope.title,
            scope.callout,
            // The detailed body is frozen WHATEVER the mode: it is the
            // record of the work the package covered, and §68V compares
            // against it. The summary beside it is what was READ (§76O).
            renderScopeBody(scope),
            scope.rootIds.join(","),
            (sortOrder += 10),
            scope.presentation.mode,
            scope.presentation.mode === "detailed" ? "" : scope.presentation.text,
            renderComparableBody(scope),
          ],
        );
      }
    });
    return id;
  }

  async listPackages(projectId: string): Promise<BidPackageSummary[]> {
    const rows = await this.db.select<{
      id: string;
      label: string;
      kind: string;
      trades: string;
      design_id: string | null;
      design_revision: number | null;
      created_at: string;
      n: number;
    }>(
      `SELECT p.id, p.label, p.kind, p.trades, p.design_id, p.design_revision, p.created_at,
              (SELECT COUNT(*) FROM bid_package_scopes s WHERE s.package_id = p.id) AS n
         FROM bid_packages p WHERE p.project_id = ? ORDER BY p.created_at DESC`,
      [projectId],
    );
    return rows.map((row) => ({
      id: row.id,
      label: row.label,
      kind: row.kind,
      trades: row.trades ? (row.trades.split(",") as TradeId[]) : [],
      designId: row.design_id,
      designRevision: row.design_revision === null ? null : Number(row.design_revision),
      createdAt: row.created_at,
      scopeCount: Number(row.n),
    }));
  }

  /** The scopes a package stated, exactly as it stated them. */
  async packageScopes(packageId: string): Promise<
    Array<{
      scopeKey: string; kind: string; tradeId: string; title: string; callout: string | null;
      body: string; comparableBody: string | null; rootIds: string[]; presentationMode: BidPresentationMode; summaryText: string;
    }>
  > {
    const rows = await this.db.select<{
      scope_key: string;
      kind: string;
      trade_id: string;
      title: string;
      callout: string | null;
      body: string;
      comparable_body: string | null;
      root_ids: string;
      presentation_mode: string | null;
      summary_text: string | null;
    }>(
      `SELECT scope_key, kind, trade_id, title, callout, body, root_ids, presentation_mode, summary_text, comparable_body
         FROM bid_package_scopes WHERE package_id = ? ORDER BY sort_order`,
      [packageId],
    );
    return rows.map((row) => ({
      scopeKey: row.scope_key,
      kind: row.kind,
      tradeId: row.trade_id,
      title: row.title,
      callout: row.callout,
      body: row.body,
      comparableBody: row.comparable_body,
      rootIds: row.root_ids ? row.root_ids.split(",") : [],
      // A package issued before §76 has no mode recorded: it was detailed,
      // because that is all there was.
      presentationMode: (row.presentation_mode as BidPresentationMode | null) ?? "detailed",
      summaryText: row.summary_text ?? "",
    }));
  }

  /* ---------------- quotes ---------------- */

  async recordQuote(input: {
    projectId: string;
    designId: string | null;
    packageId: string | null;
    contractor: string;
    quotedOn: string | null;
    amountCents: number;
    /** Components of the total (spec §79G) — null means "not split". */
    materialCents?: number | null;
    laborCents?: number | null;
    /** Optional per-work-item breakdown (§79K); each line's own arithmetic
     * is validated, and blank sides stay UNKNOWN, never zero. */
    lines?: Array<{
      groupKey: string;
      label: string;
      materialCents: number | null;
      laborCents: number | null;
      totalCents: number | null;
      materialResponsibility?: "contractor" | "owner" | "mixed" | null;
    }>;
    notes: string;
    coverage: string[];
  }): Promise<string> {
    const id = generateId();
    const now = new Date().toISOString();
    // The header's own arithmetic (§79G): a stated split must sum.
    const header = resolveQuoteAmounts(input.materialCents ?? null, input.laborCents ?? null, input.amountCents);
    if (!header.ok) throw new Error(header.problem);
    await this.db.transaction(async () => {
      await this.db.execute(
        `INSERT INTO contractor_quotes
           (id, project_id, design_id, package_id, contractor, quoted_on, amount_cents, material_cents, labor_cents, notes, selected_at, archived_at, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, NULL, NULL, ?, ?)`,
        [
          id, input.projectId, input.designId, input.packageId, input.contractor, input.quotedOn,
          header.amounts.totalCents, header.amounts.materialCents, header.amounts.laborCents,
          input.notes, now, now,
        ],
      );
      for (const key of [...new Set(input.coverage)]) {
        await this.db.execute("INSERT INTO contractor_quote_coverage (quote_id, scope_key) VALUES (?, ?)", [id, key]);
      }
      let sort = 0;
      for (const line of input.lines ?? []) {
        const resolved = resolveQuoteAmounts(line.materialCents, line.laborCents, line.totalCents);
        if (!resolved.ok) throw new Error(`${line.label}: ${resolved.problem}`);
        await this.db.execute(
          `INSERT INTO contractor_quote_lines
             (id, quote_id, group_key, label, material_cents, labor_cents, total_cents, material_responsibility, sort_order, created_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            generateId(), id, line.groupKey, line.label,
            resolved.amounts.materialCents, resolved.amounts.laborCents, resolved.amounts.totalCents,
            line.materialResponsibility ?? null, (sort += 10), now,
          ],
        );
      }
    });
    return id;
  }

  async listQuotes(projectId: string): Promise<ContractorQuote[]> {
    const [quoteRows, coverageRows, attachmentRows, lineRows] = await Promise.all([
      this.db.select<{
        id: string; project_id: string; design_id: string | null; package_id: string | null;
        contractor: string; quoted_on: string | null; amount_cents: number;
        material_cents: number | null; labor_cents: number | null; notes: string;
        selected_at: string | null; created_at: string;
      }>(
        `SELECT id, project_id, design_id, package_id, contractor, quoted_on, amount_cents,
                material_cents, labor_cents, notes, selected_at, created_at
           FROM contractor_quotes WHERE project_id = ? AND archived_at IS NULL ORDER BY created_at`,
        [projectId],
      ),
      this.db.select<{ quote_id: string; scope_key: string }>(
        `SELECT c.quote_id, c.scope_key FROM contractor_quote_coverage c
           JOIN contractor_quotes q ON q.id = c.quote_id WHERE q.project_id = ?`,
        [projectId],
      ),
      this.db.select<{ quote_id: string; attachment_id: string }>(
        `SELECT a.quote_id, a.attachment_id FROM contractor_quote_attachments a
           JOIN contractor_quotes q ON q.id = a.quote_id WHERE q.project_id = ?`,
        [projectId],
      ),
      this.db.select<{
        id: string; quote_id: string; group_key: string; label: string;
        material_cents: number | null; labor_cents: number | null; total_cents: number;
        material_responsibility: string | null;
      }>(
        `SELECT l.id, l.quote_id, l.group_key, l.label, l.material_cents, l.labor_cents, l.total_cents, l.material_responsibility
           FROM contractor_quote_lines l JOIN contractor_quotes q ON q.id = l.quote_id
          WHERE q.project_id = ? ORDER BY l.sort_order`,
        [projectId],
      ),
    ]);
    const linesBy = new Map<string, ContractorQuoteLine[]>();
    for (const row of lineRows) {
      const list = linesBy.get(row.quote_id) ?? [];
      list.push({
        id: row.id,
        groupKey: row.group_key,
        label: row.label,
        materialCents: row.material_cents === null ? null : Number(row.material_cents),
        laborCents: row.labor_cents === null ? null : Number(row.labor_cents),
        totalCents: Number(row.total_cents),
        materialResponsibility: (row.material_responsibility as never) ?? null,
      });
      linesBy.set(row.quote_id, list);
    }
    const coverageBy = new Map<string, string[]>();
    for (const row of coverageRows) {
      const list = coverageBy.get(row.quote_id) ?? [];
      list.push(row.scope_key);
      coverageBy.set(row.quote_id, list);
    }
    const attachmentsBy = new Map<string, string[]>();
    for (const row of attachmentRows) {
      const list = attachmentsBy.get(row.quote_id) ?? [];
      list.push(row.attachment_id);
      attachmentsBy.set(row.quote_id, list);
    }
    return quoteRows.map((row) => ({
      id: row.id,
      projectId: row.project_id,
      designId: row.design_id,
      packageId: row.package_id,
      contractor: row.contractor,
      quotedOn: row.quoted_on,
      amountCents: Number(row.amount_cents),
      materialCents: row.material_cents === null ? null : Number(row.material_cents),
      laborCents: row.labor_cents === null ? null : Number(row.labor_cents),
      lines: linesBy.get(row.id) ?? [],
      notes: row.notes,
      selectedAt: row.selected_at,
      createdAt: row.created_at,
      coverage: coverageBy.get(row.id) ?? [],
      attachmentIds: attachmentsBy.get(row.id) ?? [],
    }));
  }

  /** Correct the existing source, retaining its complete prior evidence.
   * A client-generated request identity makes an uncertain retry exact-once. */
  async reviseQuote(input: {projectId:string;quoteId:string;requestId:string;expected:string;
    amountCents:number;quotedOn:string|null;notes:string;coverage:string[];
    materialCents:number|null;laborCents:number|null;lines:ContractorQuoteLine[];
    reason:string;confirmSelected:boolean}):Promise<void>{
    if(!input.requestId || input.requestId.length>100 || !input.reason.trim())throw new Error('Give this correction a reason.');
    if(!Number.isSafeInteger(input.amountCents)||input.amountCents<0)throw new Error('Enter a nonnegative amount in exact cents.');
    if(input.quotedOn && (!/^\d{4}-\d{2}-\d{2}$/.test(input.quotedOn)||!Number.isFinite(Date.parse(input.quotedOn))||new Date(input.quotedOn).toISOString().slice(0,10)!==input.quotedOn))throw new Error('Choose a valid quote date.');
    const header=resolveQuoteAmounts(input.materialCents,input.laborCents,input.amountCents);
    if(!header.ok)throw new Error(header.problem);
    const command=JSON.stringify(input);
    await this.db.transaction(async()=>{
      const retry=await this.db.select<{command_json:string}>('SELECT command_json FROM contractor_quote_revisions WHERE request_id=?',[input.requestId]);
      if(retry.length){if(retry[0].command_json!==command)throw new Error('This correction request was already used for different values.');return;}
      const before=(await this.listQuotes(input.projectId)).find(q=>q.id===input.quoteId);
      if(!before)throw new Error('This quote is unavailable in this project.');
      if(JSON.stringify(before)!==input.expected)throw new Error('This quote changed. Reopen it before correcting it.');
      if(before.selectedAt&&!input.confirmSelected)throw new Error('Confirm the changed selected quote and its coverage before saving.');
      const model=await this.model(input.projectId,before.designId);
      const keys=new Set(model.scopes.map(scope=>scope.key));
      if(!input.coverage.length||input.coverage.some(key=>!keys.has(key)))throw new Error('Confirm current work coverage for this correction.');
      for(const line of input.lines){const resolved=resolveQuoteAmounts(line.materialCents,line.laborCents,line.totalCents);if(!resolved.ok)throw new Error(`${line.label}: ${resolved.problem}`);}
      if(input.lines.length&&input.lines.reduce((sum,line)=>sum+line.totalCents,0)!==input.amountCents)throw new Error('Line items must reconcile to the corrected total.');
      const now=new Date().toISOString();
      await this.db.execute('UPDATE contractor_quotes SET amount_cents=?,material_cents=?,labor_cents=?,quoted_on=?,notes=?,updated_at=? WHERE id=?',[input.amountCents,input.materialCents,input.laborCents,input.quotedOn,input.notes,now,before.id]);
      await this.db.execute('DELETE FROM contractor_quote_coverage WHERE quote_id=?',[before.id]);
      for(const key of new Set(input.coverage))await this.db.execute('INSERT INTO contractor_quote_coverage(quote_id,scope_key) VALUES (?,?)',[before.id,key]);
      const priorLineTimes=await this.db.select<{id:string;created_at:string}>('SELECT id,created_at FROM contractor_quote_lines WHERE quote_id=?',[before.id]);
      await this.db.execute('DELETE FROM contractor_quote_lines WHERE quote_id=?',[before.id]);
      let order=0;for(const line of input.lines)await this.db.execute('INSERT INTO contractor_quote_lines(id,quote_id,group_key,label,material_cents,labor_cents,total_cents,material_responsibility,sort_order,created_at) VALUES (?,?,?,?,?,?,?,?,?,?)',[line.id,before.id,line.groupKey,line.label,line.materialCents,line.laborCents,line.totalCents,line.materialResponsibility,order+=10,priorLineTimes.find(value=>value.id===line.id)?.created_at??now]);
      if(before.selectedAt)await this.selectQuote(before.id,true);
      const after=(await this.listQuotes(input.projectId)).find(q=>q.id===before.id)!;
      await this.db.execute('INSERT INTO contractor_quote_revisions(request_id,quote_id,project_id,before_json,after_json,command_json,reason,created_at) VALUES (?,?,?,?,?,?,?,?)',[input.requestId,before.id,input.projectId,JSON.stringify(before),JSON.stringify(after),command,input.reason.trim(),now]);
    });
  }

  async quoteHistory(projectId:string,quoteId:string){
    return this.db.select<{before_json:string;after_json:string;reason:string;created_at:string}>('SELECT before_json,after_json,reason,created_at FROM contractor_quote_revisions WHERE project_id=? AND quote_id=? ORDER BY created_at,request_id',[projectId,quoteId]);
  }

  /**
   * Choose which price the project should believe (§68T).
   *
   * Selecting is a PROPOSED-cost decision and creates no transaction: it is
   * not a purchase, not an allocation and not an actual expense. Earlier
   * bids are never overwritten — deselecting one and selecting another
   * leaves both, because what other people quoted is part of the record.
   */
  async selectQuote(quoteId: string, selected: boolean): Promise<void> {
    const now = new Date().toISOString();
    if (!selected) {
      await this.db.execute("UPDATE contractor_quotes SET selected_at = NULL, updated_at = ? WHERE id = ?", [now, quoteId]);
      return;
    }
    await this.db.transaction(async () => {
      const rows = await this.db.select<{ project_id: string }>(
        "SELECT project_id FROM contractor_quotes WHERE id = ?",
        [quoteId],
      );
      const projectId = rows[0]?.project_id;
      if (!projectId) return;
      // Only one selection per covered scope: a second selection for the
      // same work would leave the estimate asking which price it means.
      await this.db.execute(
        `UPDATE contractor_quotes SET selected_at = NULL, updated_at = ?
          WHERE project_id = ? AND selected_at IS NOT NULL AND id <> ?
            AND id IN (SELECT quote_id FROM contractor_quote_coverage
                        WHERE scope_key IN (SELECT scope_key FROM contractor_quote_coverage WHERE quote_id = ?))`,
        [now, projectId, quoteId, quoteId],
      );
      await this.db.execute("UPDATE contractor_quotes SET selected_at = ?, updated_at = ? WHERE id = ?", [now, now, quoteId]);
    });
  }

  async attachToQuote(quoteId: string, attachmentId: string): Promise<void> {
    await this.db.execute(
      "INSERT OR IGNORE INTO contractor_quote_attachments (quote_id, attachment_id) VALUES (?, ?)",
      [quoteId, attachmentId],
    );
  }

  async archiveQuote(quoteId: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute("UPDATE contractor_quotes SET archived_at = ?, updated_at = ? WHERE id = ?", [now, now, quoteId]);
  }
}

/** A scope as frozen text, for a package that has been issued. */
export function renderScopeBody(scope: BidScope): string {
  return renderIssuedScopeBody(scope);
}
